import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { startOfDay } from "date-fns/startOfDay";
import { isToday } from "date-fns/isToday";
import { isYesterday } from "date-fns/isYesterday";
import { format } from "date-fns/format";
import type { Locale } from "date-fns/locale";
import { getDateFnsLocale } from "@tellescope/utils";
import type { EnduserObservation } from "@tellescope/types-models";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

export interface HistoryDayGroup {
  dayKey: string;
  /** Pre-translated for "Today"/"Yesterday"; a raw formatted date string otherwise — render as-is, do not pass through t(). */
  dayLabel: string;
  /** All of the day's weight/food/water entries together, sorted descending by time. */
  entries: EnduserObservation[];
}

const dayLabelFor = (date: Date, locale: Locale, t: (key: string) => string): string => {
  if (isToday(date)) return t("Today");
  if (isYesterday(date)) return t("Yesterday");
  return format(date, "EEEE, MMMM d", { locale });
};

export const useTrackerHistory = (state: TrackerState) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);
  const groupedByDay = useMemo<HistoryDayGroup[]>(() => {
    const all = [...state.weightEntries, ...state.waterEntries, ...state.foodEntries];
    const groups = new Map<string, { date: Date; entries: EnduserObservation[] }>();

    for (const entry of all) {
      const date = startOfDay(new Date(entry.timestamp));
      const key = date.toISOString();
      const group = groups.get(key) ?? { date, entries: [] };
      group.entries.push(entry);
      groups.set(key, group);
    }

    return [...groups.entries()]
      .sort((a, b) => b[1].date.getTime() - a[1].date.getTime())
      .map(([dayKey, { date, entries }]) => ({
        dayKey,
        dayLabel: dayLabelFor(date, locale, t),
        entries: entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
      }));
  }, [state.weightEntries, state.waterEntries, state.foodEntries, locale, t]);

  const hasAnyEntries = state.weightEntries.length > 0 || state.foodEntries.length > 0 || state.waterEntries.length > 0;

  return { groupedByDay, hasAnyEntries };
};
